import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { SuppliersService } from '../../../../services/suppliers/suppliers.service';
import { AdminSuppliersList } from '../../../../pages/admin-dashboard/admin-supplier/admin-supplier.model';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import {
  buildPageNumbers,
  getEndItem,
  getStartItem,
  LoadState,
} from '../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { SupplierFiltersComponent } from './supplier-filters-component/supplier-filters-component';
import { SupplierFilters, DEFAULT_SUPPLIER_FILTERS } from './supplier-filters-component/supplier-filters-component.model';
import { SupplierTableComponent } from './supplier-table-component/supplier-table-component';

@Component({
  selector: 'app-admin-list-suppliers-component',
  imports: [SupplierFiltersComponent, SupplierTableComponent, Pagination, ConfirmationDialog, SuccessfulNotification],
  templateUrl: './admin-list-suppliers-component.html',
  styleUrl: './admin-list-suppliers-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminListSuppliersComponent implements OnInit {
  private router = inject(Router);
  private suppliersService = inject(SuppliersService);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  isArabic = computed(() => this.locale.startsWith('ar'));

  filters = signal<SupplierFilters>({ ...DEFAULT_SUPPLIER_FILTERS });
  suppliers = signal<AdminSuppliersList[]>([]);
  state = signal<LoadState>('loading');

  currentPage = signal(1);
  itemsPerPage = signal(25);

  totalItems = signal(0);
  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.itemsPerPage())));
  startItem = computed(() => getStartItem(this.currentPage(), this.itemsPerPage(), this.totalItems()));
  endItem = computed(() => getEndItem(this.currentPage(), this.itemsPerPage(), this.totalItems()));
  pageNumbers = computed(() => buildPageNumbers(this.currentPage(), this.totalPages()));
  isFiltersApplied = computed(() => {
    const f = this.filters();
    return !!(f.supplierTitle || f.createdOnFrom || f.createdOnTo);
  });

  supplierToDelete = signal<AdminSuppliersList | null>(null);
  deleting = signal(false);
  deleteError = signal('');
  showSuccessMessage = signal(false);

  supplierToToggle = signal<{ supplier: AdminSuppliersList; activate: boolean } | null>(null);
  toggling = signal(false);
  toggleError = signal('');

  toggleTitle = computed(() => {
    const activate = this.supplierToToggle()?.activate;
    if (this.isArabic()) return activate ? 'تفعيل المورد' : 'إلغاء تفعيل المورد؟';
    return activate ? 'Activate Supplier?' : 'Deactivate Supplier?';
  });
  toggleConfirm = computed(() => {
    const activate = this.supplierToToggle()?.activate;
    if (this.isArabic()) return activate ? 'تفعيل' : 'إلغاء التفعيل';
    return activate ? 'Activate' : 'Deactivate';
  });
  toggleDescription = computed(() => {
    const target = this.supplierToToggle();
    const name = (this.isArabic() ? target?.supplier.titleAr : target?.supplier.titleEn) || '';
    if (this.isArabic()) {
      return `${target?.activate ? 'هل أنت متأكد من تفعيل' : 'هل أنت متأكد من إلغاء تفعيل'} ${name}؟`;
    }
    return `Are you sure you want to ${target?.activate ? 'activate' : 'deactivate'} ${name}?`;
  });

  deleteTitle = $localize`:@@adminSuppliersActionDelete:Delete Supplier?`;
  deleteCancel = $localize`:@@adminProductsDeleteCancel:Cancel`;
  deleteConfirm = $localize`:@@adminProductsDeleteConfirmButton:Delete`;
  deleteSuccess = $localize`:@@adminSuppliersDeleteSuccess:Supplier deleted successfully!`;
  deleteDescription = computed(() => {
    const supplier = this.supplierToDelete();
    const name = (this.isArabic() ? supplier?.titleAr : supplier?.titleEn) || supplier?.titleEn || '';
    return `${$localize`:@@adminProductsDeleteConfirm:Are you sure you want to delete`} ${name}?`;
  });

  ngOnInit() {
    this.loadSuppliers();
  }

  loadSuppliers() {
    this.state.set('loading');
    const filters = this.filters();

    const subscription = this.suppliersService
      .getAllSuppliers({
        supplierTitle: filters.supplierTitle || undefined,
        pageNum: this.currentPage() - 1,
        pageSize: this.itemsPerPage(),
        createdOnFrom: filters.createdOnFrom || undefined,
        createdOnTo: filters.createdOnTo || undefined,
        orderBy: filters.orderBy,
        orderDir: filters.orderDir,
        isActive: true,
      })
      .subscribe({
        next: (res) => {
          this.suppliers.set(res.list ?? []);
          this.totalItems.set(res.total);
          this.state.set('success');
        },
        error: () => this.state.set('error'),
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  applyFilters(filters: SupplierFilters) {
    this.filters.set(filters);
    this.currentPage.set(1);
    this.loadSuppliers();
  }

  resetFilters() {
    this.filters.set({ ...DEFAULT_SUPPLIER_FILTERS });
    this.currentPage.set(1);
    this.loadSuppliers();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadSuppliers();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadSuppliers();
  }

  onViewSupplier(id: number) {
    this.router.navigate(['/admin/suppliers/view-supplier', id]);
  }

  onEditSupplier(id: number) {
    this.router.navigate(['/admin/suppliers/edit-supplier', id]);
  }

  openDeleteDialog(id: number) {
    this.deleteError.set('');
    this.supplierToDelete.set(this.suppliers().find((c) => c.id === id) ?? null);
  }

  closeDeleteDialog() {
    this.supplierToDelete.set(null);
  }

  confirmDelete() {
    const supplier = this.supplierToDelete();
    if (!supplier) return;

    this.deleting.set(true);
    this.deleteError.set('');
    const subscription = this.suppliersService
      .deleteSupplier(supplier.id)
      .pipe(finalize(() => this.deleting.set(false)))
      .subscribe({
        next: () => {
          this.closeDeleteDialog();
          this.showSuccessMessage.set(true);
          setTimeout(() => this.showSuccessMessage.set(false), 3000);
          if (this.suppliers().length === 1 && this.currentPage() > 1) {
            this.currentPage.update((page) => page - 1);
          }
          this.loadSuppliers();
        },
        error: (error) =>
          this.deleteError.set(
            this.isArabic()
              ? error?.messageAr || 'حدث بعض الخطأ'
              : error?.messageEn || 'Error deleting supplier. Please try again later.',
          ),
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  openToggleDialog(id: number, activate: boolean) {
    const supplier = this.suppliers().find((c) => c.id === id);
    if (!supplier) return;
    this.toggleError.set('');
    this.supplierToToggle.set({ supplier, activate });
  }

  closeToggleDialog() {
    this.supplierToToggle.set(null);
  }

  confirmToggle() {
    const target = this.supplierToToggle();
    if (!target) return;

    this.toggling.set(true);
    this.toggleError.set('');
    const request = target.activate
      ? this.suppliersService.activateSupplier(target.supplier.id)
      : this.suppliersService.deactivateSupplier(target.supplier.id);
    const subscription = request.pipe(finalize(() => this.toggling.set(false))).subscribe({
      next: () => {
        this.suppliers.update((list) =>
          list.map((c) => (c.id === target.supplier.id ? { ...c, isActive: target.activate } : c)),
        );
        this.closeToggleDialog();
      },
      error: (error) =>
        this.toggleError.set(
          this.isArabic()
            ? error?.messageAr || 'حدث بعض الخطأ'
            : error?.messageEn || 'Something went wrong. Please try again later.',
        ),
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
